import React, { useEffect, useState } from 'react';
import { useToast } from '../../../components/ToastContext';
import { loadFileInputs, planSales, itemSaleCsv, downloadItemSale } from './aeFile';
import ClaimSoftReturn from './ClaimSoftReturn';
import ListGrid from '../../../components/ListGrid';
import { Section } from './panelParts';

/*
  THE ACCOUNT EDGE FILE, at the foot of a run. Ordering stage 5, 12 October 2026.

  Shows the customer sales the saved run makes, one per vendor ordered from and
  one for Austin, and gives the file as one download for the job, his answer of
  12 October. **Made from the run as last saved**, so a change not yet saved is
  not in it, and the screen says so rather than letting the two drift.
*/
const money = n => '$' + Number(n || 0).toFixed(2);

export default function AeFileSection({ job, lines, unsaved, userId, finished = false, onFinished }) {
  const toast = useToast();
  const [inputs, setInputs] = useState(null);
  const [failed, setFailed] = useState(null);

  useEffect(() => {
    let live = true;
    setInputs(null);
    loadFileInputs(job, lines).then(r => {
      if (!live) return;
      if (r.error) { setFailed(r.error.message); return; }
      setFailed(null);
      setInputs(r);
    });
    return () => { live = false; };
  }, [job, lines]);

  if (failed) return <Section title="The Account Edge file"><p style={{ color: 'var(--critical-ink)', margin: 0, fontSize: 14 }}>It could not be prepared just now: {failed}</p></Section>;
  if (!inputs) return <Section title="The Account Edge file" note="Preparing..." />;

  const plan = planSales({ lines, location: inputs.location, ae: inputs.ae });
  const loc = inputs.location;
  const blocked = plan.problems.length > 0 || unsaved;

  const download = () => {
    downloadItemSale(itemSaleCsv({ sales: plan.sales, location: loc, discount: plan.discount }), job);
    toast.success('Account Edge file downloaded');
  };
  const warn = t => <div key={t} style={{ fontSize: 14, padding: '10px 14px', borderRadius: 12, background: 'var(--warning-bg)', color: 'var(--warning-ink)' }}>{t}</div>;

  /* Drawn on the house pieces from 12 October 2026, with the run. */
  return (
    <>
      <Section title="The Account Edge file"
        note={loc ? 'To ' + loc.addr_line_1 + ' (card ' + loc.card_id + '), ' + [loc.addr_line_2, loc.addr_line_3, loc.addr_line_4].filter(Boolean).join(', ') + '. Discount ' + Math.round(plan.discount * 100) + '%.' : undefined}
        right={<button type="button" className={'btn ' + (finished ? 'btn-quiet' : 'btn-primary')} disabled={blocked} onClick={download}>Download the Account Edge file</button>}>
        {unsaved && warn('The run has changes that are not saved. The file is made from the saved run, so save it first.')}
        {plan.problems.map(warn)}
        {plan.sales.length > 0 && (
          <ListGrid
            columns={[
              { key: 'n', label: 'Customer sale', width: 'minmax(200px, 1.4fr)' },
              { key: 'l', label: 'Lines', width: '90px', align: 'right' },
              { key: 'v', label: 'After discount', width: '140px', align: 'right' },
              { key: 'w', label: 'In Account Edge', width: 'minmax(220px, 1.6fr)' },
            ]}
            rows={plan.sales}
            keyOf={x => x.sale_key}
            cells={(x) => [
              <span style={{ fontSize: 15, fontWeight: 700 }}>{plan.sales.indexOf(x) + 1}. {x.austin ? 'From Austin' : x.name}</span>,
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{x.lines.length}</span>,
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(x.value)}</span>,
              <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{x.austin ? 'No purchase order: Austin sends it' : 'Make the ' + x.name + ' purchase order from this sale'}</span>,
            ]}
          />
        )}
        {plan.warnings.map(w => <div key={w} style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{w}</div>)}
      </Section>
      {!blocked && (
        <ClaimSoftReturn job={job} lines={lines} sales={plan.sales} userId={userId} finished={finished} onFinished={onFinished} />
      )}
    </>
  );
}
